// src/pages/StudentClassroomPage.js
import { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useAuth } from '../context/authContext';

function SkeletonCard() {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 animate-pulse space-y-3">
      <div className="h-5 w-3/4 bg-gray-100 rounded" />
      <div className="h-4 w-1/2 bg-gray-100 rounded" />
    </div>
  );
}

function Pill({ children, className = '' }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs border ${className}`}>
      {children}
    </span>
  );
}

function LevelBadge({ level }) {
  const lv = (level || 'beginner').toLowerCase();
  const cls =
    lv === 'advanced'
      ? 'bg-red-100 text-red-800 border-red-200'
      : lv === 'intermediate'
      ? 'bg-amber-100 text-amber-800 border-amber-200'
      : 'bg-emerald-100 text-emerald-800 border-emerald-200';
  const label = lv[0]?.toUpperCase() + lv.slice(1);
  return <Pill className={cls}>{label || 'Beginner'}</Pill>;
}

export default function StudentClassroomPage() {
  const { classroomId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [classroom, setClassroom] = useState(null);
  const [lessons, setLessons] = useState([]); // [{id,title,level,created_at?}]
  const [progressMap, setProgressMap] = useState({});
  const [error, setError] = useState('');

  // UI controls
  const [q, setQ] = useState('');
  const [levelFilter, setLevelFilter] = useState('all');
  const [sortBy, setSortBy] = useState('title'); // 'title' | 'recent'

  useEffect(() => {
    if (!user) navigate('/login');
  }, [user, navigate]);

  const load = useCallback(async () => {
    if (!classroomId) return;
    setLoading(true);
    setError('');
    try {
      // Classroom
      const { data: classroomData, error: classErr } = await supabase
        .from('classrooms')
        .select('*')
        .eq('id', classroomId)
        .maybeSingle();
      if (classErr || !classroomData) throw classErr || new Error('Classroom not found');
      setClassroom(classroomData);

      // Lessons assigned to classroom
      // Expect rows like { lesson: { id, title, level, created_at? } }
      const { data: lessonsData, error: lessonsErr } = await supabase
        .from('classroom_lessons')
        .select('lesson:lessons(id, title, level, created_at)')
        .eq('classroom_id', classroomId);
      if (lessonsErr) throw lessonsErr;

      const formatted = (lessonsData || [])
        .map((row) => row?.lesson)
        .filter(Boolean);
      setLessons(formatted);

      // Progress for this student in this classroom
      if (user?.id) {
        const { data: progressData, error: progErr } = await supabase
          .from('classroom_lesson_progress')
          .select('lesson_id, completed, teacher_score, teacher_feedback, updated_at')
          .eq('student_id', user.id)
          .eq('classroom_id', classroomId);
        if (progErr) throw progErr;

        const map = {};
        (progressData || []).forEach((p) => {
          map[p.lesson_id] = {
            completed: !!p.completed,
            teacher_score: p.teacher_score,
            teacher_feedback: p.teacher_feedback,
            updated_at: p.updated_at,
          };
        });
        setProgressMap(map);
      }
    } catch (e) {
      console.error(e);
      setError('Failed to load classroom or lessons.');
    } finally {
      setLoading(false);
    }
  }, [classroomId, user?.id]);

  useEffect(() => {
    load();
  }, [load]);

  const filteredLessons = useMemo(() => {
    let rows = [...lessons];
    const needle = q.trim().toLowerCase();
    if (levelFilter !== 'all') {
      rows = rows.filter((l) => (l.level || 'beginner').toLowerCase() === levelFilter);
    }
    if (needle) {
      rows = rows.filter((l) => (l.title || '').toLowerCase().includes(needle));
    }
    if (sortBy === 'title') {
      rows.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
    } else {
      // recent: fallback to created_at if present, else leave order
      rows.sort(
        (a, b) =>
          new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime()
      );
    }
    return rows;
  }, [lessons, q, levelFilter, sortBy]);

  if (!user) return null;

  if (error) {
    return <div className="p-6 text-red-600">{error}</div>;
  }

  if (loading || !classroom) {
    return (
      <div className="max-w-5xl mx-auto space-y-8 p-6">
        <div className="h-6 w-1/2 bg-gray-100 rounded animate-pulse" />
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-10">
      {/* Header */}
      <div className="rounded-lg border border-gray-200 bg-white p-6">
        <h2 className="text-3xl font-bold text-gray-900">{classroom.name}</h2>
        <p className="text-sm text-gray-600 mt-1">
          Join Code: <span className="font-mono">{classroom.code || '—'}</span>
        </p>
      </div>

      {/* Controls */}
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="grid sm:grid-cols-6 gap-3">
          <div className="sm:col-span-3">
            <label className="block text-xs font-medium text-gray-600 mb-1">Search lessons</label>
            <input
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              placeholder="Search by title"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs font-medium text-gray-600 mb-1">Level</label>
            <select
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              value={levelFilter}
              onChange={(e) => setLevelFilter(e.target.value)}
            >
              <option value="all">All</option>
              <option value="beginner">Beginner</option>
              <option value="intermediate">Intermediate</option>
              <option value="advanced">Advanced</option>
            </select>
          </div>
          <div className="sm:col-span-1">
            <label className="block text-xs font-medium text-gray-600 mb-1">Sort</label>
            <select
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="title">Title A to Z</option>
              <option value="recent">Recently added</option>
            </select>
          </div>
        </div>
      </div>

      {/* Lessons */}
      <div className="space-y-4">
        <h3 className="text-xl font-semibold text-gray-900">Lessons</h3>

        {filteredLessons.length === 0 ? (
          <div className="rounded-lg border border-gray-200 bg-white p-6 text-gray-700">
            {q || levelFilter !== 'all'
              ? 'No lessons match your filters.'
              : 'No lessons available for this class yet.'}
          </div>
        ) : (
          <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredLessons.map((lesson) => {
              const progress = progressMap[lesson.id] || {};
              return (
                <li
                  key={lesson.id}
                  className="rounded-lg border border-gray-200 bg-white p-4 flex flex-col gap-3"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <Link
                        to={`/modules/${lesson.level}/${lesson.id}?classroomId=${classroomId}`}
                        className="text-base font-semibold text-blue-700 hover:underline block truncate"
                        title={lesson.title}
                      >
                        {lesson.title}
                      </Link>
                      <div className="mt-1 flex flex-wrap items-center gap-2">
                        <LevelBadge level={lesson.level} />
                        {progress.completed ? (
                          <Pill className="bg-emerald-50 text-emerald-700 border-emerald-200">
                            Completed
                          </Pill>
                        ) : (
                          <Pill className="bg-gray-50 text-gray-700 border-gray-200">
                            Not started
                          </Pill>
                        )}
                        {typeof progress.teacher_score === 'number' && (
                          <Pill className="bg-indigo-50 text-indigo-700 border-indigo-200">
                            Score: {progress.teacher_score}/100
                          </Pill>
                        )}
                      </div>
                    </div>
                    <Link
                      to={`/modules/${lesson.level}/${lesson.id}?classroomId=${classroomId}`}
                      className={`shrink-0 px-3 py-1.5 rounded text-sm ${
                        progress.completed
                          ? 'bg-gray-800 text-white hover:bg-gray-700'
                          : 'bg-blue-600 text-white hover:bg-blue-500'
                      }`}
                    >
                      {progress.completed ? 'Review' : 'Start'}
                    </Link>
                  </div>

                  {progress.teacher_feedback && (
                    <div className="text-sm">
                      <Link
                        to={`/classrooms/${classroomId}/lesson/${lesson.id}/feedback/${user.id}`}
                        className="text-purple-700 hover:underline"
                      >
                        View teacher feedback →
                      </Link>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
